import React, { useState, useEffect, useRef, useCallback } from "react";
import { shortenSido } from '../utils/address'
import { StoreItem } from "../types/storeItem";
import PhotoDetailView from "./PhotoDetailView";
import LoadingSpinner from "./LoadingSpinner";
import BottomNav from "./BottomNav";
import ReviewListCard from "./ReviewListCard";
import PlaceAddButton from "./PlaceAddButton";
import BookmarkButton from "./BookmarkButton";
import { mapApi } from "../api/map";
import { reviewApi } from "../api/review";
import { useCategoryStore } from "../stores/categoryIconStore";
import { BOTTOM_NAV_HEIGHT } from "../constants/layout";
import { isIOS } from "../utils/platform";
import { useLikeStore } from "../stores/likeStore";
import { useBackgroundScrollLock } from "../hooks/useBackgroundScrollLock";
import { HOME_NAV_ITEM } from "../types/navItem";
import { VisitType } from "../constants/visitType";
import { setAppStatusBarDark } from "../utils/flutterStatusBar";
import { formatScoreOneDecimal } from "../utils/scoreFormat";
import { toastUtil } from "../utils/toast";

interface StoreBottomSheetProps {
  storeId: string | null;
  storeName: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const StoreBottomSheet: React.FC<StoreBottomSheetProps> = ({
  storeId,
  storeName,
  isOpen,
  onClose,
}) => {
  const [store, setStore] = useState<StoreItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null,
  );
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [popularReviewKeywords, setPopularReviewKeywords] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [hasUserReview, setHasUserReview] = useState(false);
  /** 이 장소에 내가 남긴 기록 수 — 2 이상이면 '· N번 기록'을 붙인다 */
  const [myReviewCount, setMyReviewCount] = useState(0);
  // 저장 시트가 이 시트 위에 뜨는 동안에는 뒤에 깔린 이 시트를 감춘다.
  // 닫는 게 아니라 감추는 것이라, 저장 시트를 닫으면 그대로 다시 보인다.
  const [isSavePanelOpen, setIsSavePanelOpen] = useState(false);
  const likes = useLikeStore((state) => state.likes);
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown,
  );
  const sheetRef = useRef<HTMLDivElement>(null);

  // 북마크 훅 사용
  const contentRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const lastLoadedStoreIdRef = useRef<string | null>(null);

  // 바텀 시트 최소 높이 placeholder(측정 전). 실제 접힌 높이는 측정 후 newMinHeight로 결정되며,
  // 이 값이 그보다 크면 collapse가 안 되므로 넉넉히 작게 둔다.
  const minHeight = 120;
  // iOS 상태바(노치/시간) 안전영역 높이 측정값
  const [safeAreaTop, setSafeAreaTop] = useState(0);
  // 상단 예약 높이: 측정된 안전영역과 노치 기본값(47) 중 큰 값 + 여백(12).
  // env() 프로브가 웹뷰에서 0을 반환해도 최소 47을 보장해 노치/시간 영역을 침범하지 않음.
  const topReserve = Math.max(safeAreaTop, 47) + 12;
  // 바텀 시트 최대 높이: 화면의 일정 비율을 넘지 않게 캡(지도 컨텍스트 유지) + 상단 안전영역 보호
  // 렌더 높이 = currentHeight + BOTTOM_NAV_HEIGHT 이므로 바텀내비도 빼야 상단이 상태바를 안 넘김
  const SHEET_MAX_RATIO = 0.75; // 시트가 화면에서 차지하는 최대 비율
  const maxHeight = Math.max(
    minHeight,
    Math.min(
      Math.floor(window.innerHeight - BOTTOM_NAV_HEIGHT - topReserve),
      Math.floor(window.innerHeight * SHEET_MAX_RATIO - BOTTOM_NAV_HEIGHT),
    ),
  );
  const [currentHeight, setCurrentHeight] = useState(minHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [calculatedMinHeight, setCalculatedMinHeight] = useState(minHeight);
  const prevIsOpenRef = useRef(false);
  const prevStoreIdRef = useRef<string | null>(null);

  // 리뷰가 없으면 시트를 헤더(장소정보+키워드+실선) 높이로 고정 — 그 아래 빈 공간 방지
  const hasNoReviews = !!store && (store.reviewCount ?? 0) === 0;
  const effectiveMaxHeight = hasNoReviews ? calculatedMinHeight : maxHeight;

  // 저장 시트를 닫고 돌아왔을 때는 확장 상태를 유지하지 않고 항상 최소 높이로
  // 되돌린다. 확장된 채로 다시 나타나면 뒤 지도가 가려진 상태로 복귀해 어색하다.
  // "저장 시트가 닫히는 순간"에만 동작해야 한다. isSavePanelOpen이 false인 동안
  // 계속 걸리면 사용자가 직접 확장하는 것도 즉시 되감긴다.
  const prevSavePanelOpenRef = useRef(false);
  useEffect(() => {
    if (prevSavePanelOpenRef.current && !isSavePanelOpen) {
      setCurrentHeight(calculatedMinHeight);
    }
    prevSavePanelOpenRef.current = isSavePanelOpen;
  }, [isSavePanelOpen, calculatedMinHeight]);

  useEffect(() => {
    setAppStatusBarDark(isOpen);
    return () => setAppStatusBarDark(false);
  }, [isOpen]);

  // 시트 열린 동안 배경 스크롤 잠금 (시트 내부 스크롤 영역은 허용)
  useBackgroundScrollLock(isOpen);

  // iOS 안전영역(상태바) 높이 측정 — env(safe-area-inset-top)
  useEffect(() => {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top);visibility:hidden;pointer-events:none;";
    document.body.appendChild(probe);
    setSafeAreaTop(probe.getBoundingClientRect().height);
    probe.remove();
  }, []);

  // 모달이 처음 열릴 때 또는 storeId가 변경될 때만 데이터 로드
  const shouldLoadData =
    isOpen &&
    storeId &&
    (!prevIsOpenRef.current || prevStoreIdRef.current !== storeId);

  if (shouldLoadData) {
    prevIsOpenRef.current = true;
    prevStoreIdRef.current = storeId;

    // 초기값 설정
    setStore({
      id: storeId,
      name: "",
      address: "",
      phoneNumber: "",
      position: { lat: 0, lng: 0 },
      type: "restaurant",
      businessHours: [],
      currentStatus: "closed",
      score: null,
      rating: 0,
      reviewCount: 0,
      reviews: [],
      isBookmarked: false,
      isFollowing: false,
      bookmarkCount: 0,
    });
    setCurrentHeight(minHeight);
    setIsLoading(true);
    setIsLoadingReviews(true);
    lastLoadedStoreIdRef.current = storeId;

    // 가게 정보 가져오기
    mapApi
      .getPlace(storeId)
      .then((placeData) => {
        setStore((prevStore) => {
          if (!prevStore) return null;
          return {
            ...prevStore,
            name: placeData.name,
            address: placeData.address || "",
            phoneNumber: placeData.phone_number || "",
            position: { lat: placeData.lat, lng: placeData.lng },
            type: placeData.category,
            subCategory: placeData.sub_category ?? null,
            score: placeData.score,
            isBookmarked: placeData.is_bookmarked || false,
            bookmarkCount: placeData.bookmark_count || 0,
          };
        });
        // 사용자 리뷰 작성 여부 및 키워드 설정
        setHasUserReview(placeData.has_user_review);
        setMyReviewCount(placeData.my_review_count ?? 0);
        setPopularReviewKeywords(placeData.popular_review_keywords ?? []);
      })
      .catch((error) => {
        console.error("가게 정보 가져오기 실패:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // 리뷰 목록 가져오기
    reviewApi
      .getReviewListByPlace(parseInt(storeId))
      .then((reviewData) => {
        // 리뷰 데이터 변환
        const reviews = reviewData.reviews.map((review) => {
          // 방금 받아온 서버 값을 정본으로 삼는다. 예전처럼 전역 캐시를 우선하면
          // 남이 누른 좋아요가 화면에 영영 안 나타난다.
          // (내 토글이 처리 중인 리뷰는 syncFromServer가 건너뛴다)
          useLikeStore
            .getState()
            .syncFromServer(review.id, review.is_liked, review.like_count);

          const likeState = useLikeStore.getState().likes.get(review.id);
          const isLiked = likeState?.isLiked ?? review.is_liked;
          const likeCount = likeState?.likeCount ?? review.like_count;

          return {
            id: review.id,
            userId: review.user_id,
            username: review.username || undefined,
            nickname: review.nickname,
            profileImageUrl: review.profile_image_url || undefined,
            rating: review.rating,
            images: review.images.length > 0 ? review.images : undefined,
            content: review.content,
            likeCount,
            isLiked,
            createdAt: review.created_at || "",
            visitDate: review.visit_date || null,
            visitType: review.visit_type as VisitType,
            hideScore: review.hide_score || false,
          };
        });

        const averageRating =
          reviewData.average_rating > 0
            ? Math.min(10, Math.max(0, reviewData.average_rating))
            : 0;

        setStore((prevStore) => {
          if (!prevStore) return null;
          return {
            ...prevStore,
            rating: averageRating,
            reviewCount: reviewData.review_count,
            reviews: reviews,
          };
        });
      })
      .catch((error) => {
        console.error("리뷰 목록 가져오기 실패:", error);
      })
      .finally(() => {
        setIsLoadingReviews(false);
      });
  }

  // 모달이 닫힐 때 초기화
  if (!isOpen && prevIsOpenRef.current) {
    prevIsOpenRef.current = false;
    prevStoreIdRef.current = null;
    setStore(null);
    setIsLoading(false);
    setIsLoadingReviews(false);
    setPopularReviewKeywords([]);
    setHasUserReview(false);
    lastLoadedStoreIdRef.current = null;
  }

  // iOS: 바텀 시트 열릴 때 body 스크롤 잠금 (overflow:hidden은 iOS에서 무효, position:fixed 사용)
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // 헤더 높이 측정하여 최소 높이 설정
  useEffect(() => {
    if (
      isOpen &&
      store &&
      headerRef.current &&
      dragHandleRef.current &&
      !isLoading
    ) {
      // DOM 업데이트 후 높이 측정을 위해 약간의 지연
      const timeoutId = setTimeout(() => {
        if (headerRef.current && dragHandleRef.current) {
          const headerHeight = headerRef.current.offsetHeight;
          const dragHandleHeight = dragHandleRef.current.offsetHeight;
          // 렌더높이에 하단내비+세이프에어리어를 포함시켜 '보이는 높이 = currentHeight'가 됐으므로,
          // 접힌 높이 = 핸들 + 헤더('N명 저장'까지) + 12px 여백. 그 아래 16px갭·구분선은 안 보임(12<16).
          const newMinHeight = dragHandleHeight + headerHeight + 12;

          setCalculatedMinHeight(newMinHeight);

          // 리뷰 없으면 헤더 높이로 고정, 있으면 기존 높이 유지하되 최대치(80%)로 제한
          setCurrentHeight((prevHeight) => {
            if (hasNoReviews) return newMinHeight;
            const target =
              prevHeight < newMinHeight ? newMinHeight : prevHeight;
            return Math.min(target, maxHeight);
          });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, store?.id, isLoading]);

  // 드래그 시작
  const handleDragStart = useCallback(
    (clientY: number) => {
      isDraggingRef.current = true;
      setIsDragging(true);
      startYRef.current = clientY;
      startHeightRef.current = currentHeight;
    },
    [currentHeight],
  );

  // 드래그 중
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDraggingRef.current) return;

      const deltaY = startYRef.current - clientY; // 위로 드래그하면 양수
      // 접힌(min) 상태에서 시작했으면 min 아래로도 끌려가게 해서 "닫히는 모션"을 보여준다.
      const startedCollapsed =
        startHeightRef.current <= calculatedMinHeight + 1;
      const lowerBound = startedCollapsed ? 0 : calculatedMinHeight;
      const newHeight = Math.max(
        lowerBound,
        Math.min(effectiveMaxHeight, startHeightRef.current + deltaY),
      );
      setCurrentHeight(newHeight);
    },
    [calculatedMinHeight, effectiveMaxHeight],
  );

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;

    const startedCollapsed = startHeightRef.current <= calculatedMinHeight + 1;

    setCurrentHeight((prevHeight) => {
      isDraggingRef.current = false;
      setIsDragging(false);

      // 접힌 상태에서 아래로 충분히(60px) 끌어내렸으면 시트 닫기
      if (startedCollapsed && prevHeight < calculatedMinHeight - 60) {
        onClose();
        return calculatedMinHeight; // 닫힌 뒤 재오픈 시 기본 높이로 복원
      }

      const threshold = (effectiveMaxHeight - calculatedMinHeight) / 2;
      return prevHeight > calculatedMinHeight + threshold
        ? effectiveMaxHeight
        : calculatedMinHeight;
    });
  }, [effectiveMaxHeight, calculatedMinHeight, onClose]);

  // 마우스 이벤트 핸들러
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        handleDragMove(e.clientY);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        handleDragEnd();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isOpen, handleDragMove, handleDragEnd]);

  // 터치 이벤트 핸들러
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientY);
  };

  const handleTouchEnd = () => {
    if (isDraggingRef.current) {
      handleDragEnd();
    }
  };

  // 헤더(가게 정보 섹션) 드래그 — 핸들과 동일한 모션이지만,
  // 안의 버튼/복사 등 탭과 구분하기 위해 일정 거리(8px) 이상 움직여야 드래그로 인식한다.
  const headerStartYRef = useRef(0);
  const headerDragArmedRef = useRef(false); // 임계값 넘어 드래그가 시작됐는지

  const handleHeaderTouchStart = (e: React.TouchEvent) => {
    headerStartYRef.current = e.touches[0].clientY;
    headerDragArmedRef.current = false;
  };

  const handleHeaderTouchMove = (e: React.TouchEvent) => {
    const y = e.touches[0].clientY;
    if (!headerDragArmedRef.current) {
      if (Math.abs(y - headerStartYRef.current) <= 8) return; // 탭으로 간주, 무시
      headerDragArmedRef.current = true;
      handleDragStart(y); // 현재 지점 기준으로 드래그 시작(점프 방지)
    }
    e.preventDefault();
    handleDragMove(y);
  };

  const handleHeaderTouchEnd = () => {
    if (headerDragArmedRef.current) {
      headerDragArmedRef.current = false;
      handleDragEnd();
    }
  };

  // 전역 좋아요 상태 변경 시 store.reviews 동기화
  useEffect(() => {
    if (!store) return;

    setStore((prevStore) => {
      if (!prevStore) return null;

      // 좋아요 상태가 실제로 변경되었는지 확인
      const updatedReviews = prevStore.reviews.map((review) => {
        const likeState = likes.get(review.id);
        if (likeState) {
          // 상태가 변경되지 않았으면 원본 반환
          if (
            review.isLiked === likeState.isLiked &&
            review.likeCount === likeState.likeCount
          ) {
            return review;
          }
          return {
            ...review,
            isLiked: likeState.isLiked,
            likeCount: likeState.likeCount,
          };
        }
        return review;
      });

      // 변경사항이 없으면 원본 store 반환 (무한 루프 방지)
      const hasChanges = updatedReviews.some(
        (review, index) =>
          review.isLiked !== prevStore.reviews[index]?.isLiked ||
          review.likeCount !== prevStore.reviews[index]?.likeCount,
      );

      if (!hasChanges) {
        return prevStore;
      }

      return {
        ...prevStore,
        reviews: updatedReviews,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [likes, store?.id]);

  if (!isOpen || !store) return null;

  const renderReviewsContent = () => {
    // 가게 정보가 없으면 표시 안 함
    if (!store) return null;

    // 헤더가 로딩 중인 경우 표시 안 함
    if (isLoading) return null;

    // 리뷰 목록이 로딩 중인 경우 로딩 스피너 표시
    if (isLoadingReviews) {
      return (
        <div className="w-full flex justify-center items-center py-16px">
          <LoadingSpinner size={28} borderWidth={3} />
        </div>
      );
    }

    return (
      // 마지막 리뷰 아래 여백. 리뷰 카드가 pb-4px로 끝나서 끝까지 스크롤하면
      // 날짜·방문형태 줄이 하단 메뉴바에 딱 붙어 잘린 것처럼 보였다.
      <div className="w-full flex flex-col pb-20px">
        {/* 리뷰 목록 */}
        {store.reviews.map((review) => (
          <ReviewListCard key={review.id} review={review} />
        ))}
      </div>
    );
  };

  const _buildHeader = () => {
    if (!store) return null;

    // 추가/북마크 아이콘 — 로딩 여부와 무관하게 항상 헤더 우상단
    const actionIcons = (
      <div className="flex items-center justify-end gap-8px">
        <PlaceAddButton storeId={store.id} isAdded={hasUserReview} />

        <BookmarkButton
          storeId={store.id}
          isBookmarked={store.isBookmarked ?? false}
          placeName={store.name}
          placeAddress={store.address}
          onSheetOpenChange={setIsSavePanelOpen}
        />
      </div>
    );

    // 로딩 중: 스피너를 헤더(이름+주소 2줄, ≈54px) 세로 중앙에 두고 아이콘은 우상단 고정
    if (isLoading) {
      return (
        <div ref={headerRef} className="px-16px">
          <div className="relative" style={{ minHeight: 54 }}>
            <div className="absolute top-0 right-0">{actionIcons}</div>
            <div className="absolute inset-0 flex items-center justify-center">
              <LoadingSpinner size={28} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref={headerRef} className="px-16px">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-8px">
              <h2 className="flex-1 min-w-0 font-pretendard font-700 text-18px text-brown-80%-main overflow-hidden text-ellipsis whitespace-nowrap">
                {store.name}
              </h2>
              {actionIcons}
            </div>

            {store.address && (
              <div className="mt-8px flex items-center gap-8px">
                <p className="font-pretendard font-regular text-16px text-textColorText2 overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0">
                  {shortenSido(store.address)}
                </p>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const text = store.address;
                    try {
                      if (
                        navigator.clipboard &&
                        navigator.clipboard.writeText
                      ) {
                        await navigator.clipboard.writeText(text);
                      } else {
                        // 폴백: WebView/구버전 브라우저용 execCommand
                        const ta = document.createElement("textarea");
                        ta.value = text;
                        ta.style.position = "fixed";
                        ta.style.left = "-9999px";
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                      toastUtil.success("주소가 복사되었습니다");
                    } catch (err) {
                      console.error("주소 복사 실패", err);
                      toastUtil.error("주소 복사에 실패했습니다");
                    }
                  }}
                  className="font-pretendard font-bold text-16px text-brown-80%-main flex-shrink-0"
                >
                  복사
                </button>
              </div>
            )}

            {/* 점수 및 참여자 정보 */}
            <div className="mt-16px w-full flex justify-center items-center gap-16px">
              {getCategoryIconBrown(store.type) && (
                <img
                  src={getCategoryIconBrown(store.type)!}
                  alt=""
                  className="w-36px h-36px object-contain"
                />
              )}

              {store.score !== null && store.score !== undefined ? (
                <>
                  <span className="text-style-headlineS text-brown-80%-main text-center">
                    {formatScoreOneDecimal(store.score)}
                  </span>

                  {/* 리뷰 참여자 수 */}
                  <span className="font-pretendard font-regular text-16px text-brown-80%-main text-center">
                    <strong
                      className="font-pretendard font-bold"
                      style={{ letterSpacing: "0.1em" }}
                    >
                      TASTEE
                    </strong>
                    &nbsp;
                    {store.reviewCount.toLocaleString()}명
                  </span>
                </>
              ) : (
                <>
                  <span className="text-style-headlineS text-brown-80%-main text-center">
                    N / A
                  </span>
                </>
              )}
            </div>

            {/* 태그 및 저장 정보 */}
            <div className="mt-16px flex items-center justify-between">
              {/* 카카오 카테고리 + 키워드 표시 (예: "양식 | 정보없음") */}
              <div className="flex items-center gap-4px">
                <span className="font-pretendard font-regular text-16px text-brown-80%-main">
                  {store.subCategory ? `${store.subCategory} | ` : ""}
                  {popularReviewKeywords.length > 0
                    ? popularReviewKeywords
                        .map((keyword) => keyword.name)
                        .join(", ")
                    : "정보없음"}
                  {/* 재방문 — 한 번만 갔으면 붙이지 않는다 (모든 곳에 '1번
                      기록'이 붙으면 읽을 정보가 되지 못한다) */}
                  {myReviewCount > 1 && ` · ${myReviewCount}번 기록`}
                </span>
              </div>

              {/* 북마크 저장 수 */}
              <span className="font-pretendard font-regular text-16px text-brown-80%-main">
                {store.bookmarkCount?.toLocaleString() || 0}명 저장
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hiddenWhileSaving: React.CSSProperties = isSavePanelOpen
    ? { visibility: "hidden", pointerEvents: "none" }
    : {};

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        style={hiddenWhileSaving}
        onClick={onClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center"
        style={hiddenWhileSaving}
      >
        <div className="app-container flex flex-col">
          {/* 바텀 시트 */}
          <div
            ref={sheetRef}
            className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col"
            style={{
              /*
               * 렌더높이 = currentHeight + 하단내비(60) + (iOS만) 홈인디케이터 세이프에어리어.
               * 하단바·세이프까지 덮어야 '보이는 높이 = currentHeight'로 일치해서
               * 접힌 높이 계산이 정확해진다.
               *
               * 세이프에어리어를 iOS에서만 더하는 이유
               * ----------------------------------
               * iOS는 웹뷰가 홈인디케이터 뒤까지 깔려 있어(SafeArea bottom:false)
               * 그만큼 더 덮어야 한다. 안드로이드는 SafeArea가 이미 내비바를
               * 제외하고 웹뷰를 놓는데, 그런데도 env는 값을 돌려준다.
               * 갤럭시 Z Flip4에서 48이 나왔고, 그만큼 시트가 더 열려서
               * 헤더 아래 구분선까지 보였다.
               *
               * index.css에도 같은 이유로 안드로이드에는 env를 쓰지 않는다고
               * 적혀 있는데, 이 시트만 그 규칙을 따르지 않고 있었다.
               */
              height: isIOS()
                ? `calc(${currentHeight + BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`
                : `${currentHeight + BOTTOM_NAV_HEIGHT}px`,
              transition: isDragging ? "none" : "height 0.3s ease-out",
            }}
          >
            {/* 드래그 핸들 */}
            <div
              ref={dragHandleRef}
              className="flex flex-col w-full cursor-grab active:cursor-grabbing"
              style={{ touchAction: "none" }}
              onMouseDown={(e) => handleDragStart(e.clientY)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-16px mb-16px"></div>
            </div>

            {/* 헤더 (가게 정보) — 아래로 끌면 핸들과 동일하게 시트 닫힘/리사이즈.
                안의 버튼 탭은 이동 임계값으로 구분되어 그대로 동작한다. */}
            <div
              onTouchStart={handleHeaderTouchStart}
              onTouchMove={handleHeaderTouchMove}
              onTouchEnd={handleHeaderTouchEnd}
              style={{ touchAction: "pan-y" }}
            >
              {_buildHeader()}
            </div>

            <div className="h-16px"></div>

            {/* divider */}
            <div className="w-full h-1px">
              <div className="w-full h-full bg-divider-divider-1"></div>
            </div>

            {/* 컨텐츠 - iOS에서 스크롤 영역으로 인식되도록 touch-action, overscroll-behavior 적용 */}
            <div
              ref={contentRef}
              className="flex flex-col flex-1 overflow-y-scroll scrollbar-hide"
              style={{
                minHeight: 0,
                maxHeight: `${currentHeight - calculatedMinHeight}px`,
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                // pan-y만 두면 자식(리뷰 사진 가로 스크롤)이 막히므로 가로도 허용
                touchAction: "pan-x pan-y",
              }}
            >
              {renderReviewsContent()}
            </div>

            {/* 하단 탭바 자리 확보용 스페이서(시트 높이 = currentHeight + BOTTOM_NAV_HEIGHT).
                실제 탭바는 아래 fixed BottomNav가 그린다 — 시트 flex 안에 두면
                스크롤/드래그 시 함께 움직이므로 뷰포트 하단 고정으로 분리. */}
            <div
              className="w-full shrink-0"
              style={{ height: `${BOTTOM_NAV_HEIGHT}px` }}
            ></div>
          </div>
        </div>

        {/* 하단 탭바 — 뷰포트 하단에 fixed. 시트(z-50) 래퍼의 마지막 자식이라
            시트 위에 그려지고, position:fixed라 시트 스크롤/드래그와 무관하게 고정. */}
        <BottomNav activePath={HOME_NAV_ITEM.path} />
      </div>

      {/* 사진 자세히보기 */}
      {selectedPhotoIndex !== null &&
        selectedReviewId !== null &&
        store.reviews.find((r) => r.id === selectedReviewId) && (
          <PhotoDetailView
            images={
              store.reviews.find((r) => r.id === selectedReviewId)?.images || []
            }
            initialIndex={selectedPhotoIndex}
            onClose={() => {
              setSelectedPhotoIndex(null);
              setSelectedReviewId(null);
            }}
            content={
              store.reviews.find((r) => r.id === selectedReviewId)?.content
            }
          />
        )}
    </>
  );
};

export default StoreBottomSheet;
